// New Season — the farming unit as the Farming unit editor's Meta data and
// Sensors tabs edit it, ported from production's farming unit modal
// (farming units/FarmingUnitModal.jsx, edit mode).
//
// The tabs must save exactly what that modal saves: the whole farming unit,
// its sensors included, through distributionActions.update_farming_unit.
// V1's update replaces the unit's sensors with the ones sent, so the draft
// starts from the tree's `farming_unit` (the unit's live sensors, as V1's
// GET /distribution gives them). Kept free of React and webpack aliases (the
// GeneralClasses module is passed in) so it runs under `node --test`.

// FarmingUnitModal's normalization when it opens on a farming unit, applied
// to the GeneralClasses.FarmingUnit production builds from the same JSON.
const farmingUnitForEditing = (farmingUnitJson, role, classes) => {
  const farmingUnit = new classes.FarmingUnit(
    { ...structuredClone(farmingUnitJson), role },
    farmingUnitJson.id,
  );
  const { fields, historical_data, ...rest } = farmingUnit;
  return {
    ...rest,
    name: farmingUnit.name || '',
    mondayID: farmingUnit.mondayID || '',
    country: farmingUnit.country || undefined,
    region: farmingUnit.region || undefined,
    sensors: farmingUnit.sensors || {},
  };
};

// FarmingUnitModal.updateFarmingUnitProperties: set a dotted path, creating
// missing objects on the way. Nested objects are copied rather than mutated,
// so the result is the same object production would send.
const setFarmingUnitProperty = (farmingUnit, path, value) => {
  const keys = path.split('.');
  const next = { ...farmingUnit };
  let level = next;
  keys.slice(0, -1).forEach((key) => {
    level[key] = { ...(level[key] || {}) };
    level = level[key];
  });
  level[keys[keys.length - 1]] = value;
  return next;
};

// FarmingUnitModal's isSaveOptionDisabled.
const isFarmingUnitIncomplete = (farmingUnit) =>
  farmingUnit.name === '' ||
  farmingUnit.mondayID === '' ||
  farmingUnit.country === undefined ||
  farmingUnit.region === undefined;

// FarmingUnitModal.checkValuesChangesInFarmingUnit: the "Farming Unit Edited"
// Mixpanel event's `Changes`.
const farmingUnitChanges = (current, original) => {
  const changes = {};
  const compare = (currentObj, originalObj, path) => {
    Object.keys(currentObj || {}).forEach((key) => {
      const currentValue = currentObj[key];
      const originalValue = originalObj ? originalObj[key] : undefined;
      const currentPath = path ? `${path}.${key}` : key;
      if (
        typeof currentValue === 'object' &&
        currentValue !== null &&
        !Array.isArray(currentValue)
      ) {
        compare(currentValue, originalValue, currentPath);
      } else if (currentValue !== originalValue) {
        changes[currentPath] = {
          oldValue: originalValue,
          newValue: currentValue,
        };
      }
    });
  };
  compare(current, original, '');
  return changes;
};

// What FarmingUnitModal hands to update_farming_unit.
const farmingUnitSaveBody = (farmingUnit) => {
  const { fields, historical_data, ...body } = farmingUnit;
  return body;
};

// Each tab saves on its own. Both bodies are production's whole-unit body;
// the part the other tab edits is sent as it is stored, so its unsaved edits
// stay out of this save.
// - Meta data: the draft's basic info / water treatment, the stored sensors.
const metaSaveBody = (unit, original) =>
  farmingUnitSaveBody({ ...unit, sensors: original.sensors });

// - Sensors: the stored basic info / water treatment, the draft's sensors.
const sensorsSaveBody = (unit, original) =>
  farmingUnitSaveBody({ ...original, sensors: unit.sensors });

// A re-read farming unit (after any tab's save) replaces the draft's saved
// parts; a part with unsaved edits (`dirty.meta` / `dirty.sensors`) keeps them.
const rebaseFarmingUnitDraft = (fromTree, current, dirty) => {
  if (!current) {
    return fromTree;
  }
  const base = dirty.meta ? current : fromTree;
  return {
    ...base,
    sensors: dirty.sensors ? current.sensors : fromTree.sensors,
  };
};

// "<country>/<name> <Monday ID>", the editor's title.
const farmingUnitEditTitle = (farmingUnit) =>
  farmingUnit
    ? `${farmingUnit.country || ''}/${farmingUnit.name || ''} ${farmingUnit.mondayID || ''}`.trim()
    : '';

module.exports = {
  farmingUnitForEditing,
  setFarmingUnitProperty,
  isFarmingUnitIncomplete,
  farmingUnitChanges,
  farmingUnitSaveBody,
  metaSaveBody,
  sensorsSaveBody,
  rebaseFarmingUnitDraft,
  farmingUnitEditTitle,
};
